import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { Collapse, Form, Table } from 'react-bootstrap';
import { AppState, dataActions } from 'reducers';
import { FaCheck, FaTrashAlt, FaEdit, FaAngleUp, FaAngleDown } from 'react-icons/fa';
import store from 'store';

import { Product, ProductInCartTable } from 'lib/types';
import { MdClose } from 'react-icons/md';
import AdditionalProducts from './AdditionalProducts';
import './ProductSelection.scss';
import NumberInput from 'components/NumberInput';
import { cartMethods } from 'business_logic';
import useCartMessage from 'lib/hooks/useCartMessage';
import { Messages } from 'reducers/data';
import { useDevice } from 'lib/hooks/useDevice';
import { removeCartLine } from 'lib/cart-actions';

interface SimilarProductsProps {
    selectedProduct: Product;
}

const MAX_SIMILAR_COUNT_SHOW = 6;

const SimilarProducts = ({ selectedProduct }: SimilarProductsProps) => {
    const { isDesktop } = useDevice();
    const { setShowCartMessage } = useCartMessage();

    const localCart = useSelector((state: AppState) => (state.data.orders.localCart));

    const [similarEdit, setSimilarEdit] = useState(null as number | null);
    const [similarEditQuantity, setSimilarEditQuantity] = useState(1);
    const [similarEditDimension, setSimilarEditDimension] = useState(1);
    const [similarEditIsDimension, setSimilarEditIsDimension] = useState(false);
    const [similarAdditions, setSimilarAdditions] = useState(Array<boolean>());
    const [isValidDimension, setIsValidDimension] = useState(true);
    const [isSavable, setSavable] = useState(similarEditQuantity > 0 && similarEditDimension > 0);
    const [collapseSimilar, setCollapseSimilar] = useState(false);
    const [showMoreSimilar, setShowMoreSimilar] = useState(false);
    const [similarProducts, setSimilarProducts] = useState<ProductInCartTable[]>(Array<ProductInCartTable>());

    const [maxQuantity, setMaxQuantity] = useState<number>(0);
    const [maxDimension, setMaxDimension] = useState<number>(0);
    const [maxJMPrice, setMaxJMPrice] = useState<number>(0);
    const [maxJMQuantity, setMaxJMQuantity] = useState<number>(0);
    const [maxM2Quantity, setMaxM2Quantity] = useState<number>(0);
    const [maxGrossPrice, setMaxGrossPrice] = useState<number>(0);

    const similarProductsRef = useRef<null | HTMLDivElement>(null);

    const makeGroupName = () => _.join(selectedProduct.path, ' ');

    const similarProductCount = _.filter(localCart.productsInLocalCart, (prd) => prd.product.path.join(' ') === makeGroupName()).length;

    const [collapseSimilarDetails, setCollapseSimilarDetails] = useState(Array<boolean>(similarProductCount).fill(false));

    const totalSimilarQuantity = similarEditQuantity * (similarEditIsDimension ? similarEditDimension : 1);

    const deleteProduct = (productInCart: ProductInCartTable) => {
        if (isDesktop) setShowCartMessage(Messages.cartMessage);

        removeCartLine(productInCart);
        setSimilarEdit(null);
    };

    const editProduct = (productInCart: ProductInCartTable) => {
        setSimilarEdit(productInCart.indexInCart);
        setSimilarEditQuantity(productInCart.quantity);
        setSimilarEditDimension(productInCart.dimension ?? 1);
        setSimilarEditIsDimension(productInCart.product.isDimension);
        setSimilarAdditions(productInCart.additionsSelected ?? []);
    };

    const widthOffset = 1;

    const maxQuantityWidth = useMemo(() => {
        const d = document.createElement('div');
        d.innerHTML = maxQuantity.toFixed(2);
        d.classList.add('measurer');
        document.querySelector('.ProductSelection-similarProductsTable')?.appendChild(d);
        const { width } = d.getBoundingClientRect();
        document.querySelector('.ProductSelection-similarProductsTable')?.removeChild(d);
        return width + widthOffset;
    }, [maxQuantity]);

    const maxDimensionWidth = useMemo(() => {
        const d = document.createElement('div');
        d.innerHTML = maxDimension.toFixed(3);
        d.classList.add('measurer');
        document.querySelector('.ProductSelection-similarProductsTable')?.appendChild(d);
        const { width } = d.getBoundingClientRect();
        document.querySelector('.ProductSelection-similarProductsTable')?.removeChild(d);
        return width + widthOffset;
    }, [maxDimension]);

    const maxJMPriceWidth = useMemo(() => {
        const d = document.createElement('div');
        d.innerHTML = maxJMPrice.toFixed(2);
        d.classList.add('measurer');
        document.querySelector('.ProductSelection-similarProductsTable')?.appendChild(d);
        const { width } = d.getBoundingClientRect();
        document.querySelector('.ProductSelection-similarProductsTable')?.removeChild(d);
        return width === 0 ? 30 + widthOffset : width + widthOffset;
    }, [maxJMPrice]);

    const maxJMQuantityWidth = useMemo(() => {
        const d = document.createElement('div');
        d.innerHTML = maxJMQuantity.toFixed(2);
        d.classList.add('measurer');
        document.querySelector('.ProductSelection-similarProductsTable')?.appendChild(d);
        const { width } = d.getBoundingClientRect();
        document.querySelector('.ProductSelection-similarProductsTable')?.removeChild(d);
        return width === 0 ? 30 + widthOffset : width + widthOffset;
    }, [maxJMQuantity]);

    const maxM2QuantityWidth = useMemo(() => {
        const d = document.createElement('div');
        d.innerHTML = maxM2Quantity.toFixed(2);
        d.classList.add('measurer');
        document.querySelector('.ProductSelection-similarProductsTable')?.appendChild(d);
        const { width } = d.getBoundingClientRect();
        document.querySelector('.ProductSelection-similarProductsTable')?.removeChild(d);
        return width === 0 ? 30 + widthOffset : width + widthOffset;
    }, [maxM2Quantity]);

    const maxGrossPriceWidth = useMemo(() => {
        const d = document.createElement('div');
        d.innerHTML = maxGrossPrice.toFixed(2);
        d.classList.add('measurer');
        document.querySelector('.ProductSelection-similarProductsTable')?.appendChild(d);
        const { width } = d.getBoundingClientRect();
        document.querySelector('.ProductSelection-similarProductsTable')?.removeChild(d);
        return width === 0 ? 30 + widthOffset : width + widthOffset;
    }, [maxGrossPrice]);

    const saveProduct = (product: ProductInCartTable) => {
        if (!isSavable) return;
        setSimilarEdit(null);
        store.dispatch(dataActions.editProductsInLocalCart({
            index: product.indexInCart,
            product: {
                ...product,
                quantity: similarEditQuantity,
                dimension: similarEditDimension,
                additionsSelected: similarAdditions
            }
        }));
    };

    const handleScrollTo = () => {
        if (similarProductsRef && similarProductsRef.current) similarProductsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const getProductInCartData = (productInCart: ProductInCartTable): ProductInCartTable => {
        if (similarEdit === productInCart.indexInCart) {
            return {
                ...productInCart,
                quantity: similarEditQuantity,
                dimension: similarEditDimension,
                additionsSelected: similarAdditions
            };
        }
        return { ...productInCart };
    };

    const getSimilarProductRow = (productInCart: ProductInCartTable) => {
        const productInCartData = getProductInCartData(productInCart);

        return (
            <tr key={`similar_${productInCartData.indexInCart}`}>
                <td style={{ textAlign: 'left' }}>{productInCartData.product.description}</td>
                <td>
                    <div className='price-td-div' style={{ width: similarEdit === productInCartData.indexInCart ? 'auto' : maxQuantityWidth, marginRight: '0' }}>
                        {
                            similarEdit === productInCartData.indexInCart ? (
                                <NumberInput
                                    value={similarEditQuantity}
                                    setValue={(value) => setSimilarEditQuantity(value)}
                                    disabledEdit={similarEditQuantity === 1}
                                />
                            ) : productInCartData.quantity
                        }
                    </div>
                </td>
                <td>
                    <div className='price-td-div' style={{ width: similarEdit === productInCartData.indexInCart ? 'auto' : maxDimensionWidth, marginRight: '0' }}>
                        {
                            similarEdit === productInCartData.indexInCart && productInCartData.product.isDimension && productInCartData.product.dimensionCanEdit ? (
                                <NumberInput
                                    value={similarEditDimension}
                                    setValue={(value) => setSimilarEditDimension(value)}
                                    step={0.1}
                                    min={selectedProduct.dimensionMin ?? 0.01}
                                    max={selectedProduct.dimensionMax ?? 10 ** 5}
                                    fractDigits={3}
                                    disabled={_.isEqual(productInCartData.product.unit, 'szt') || !productInCartData.product.dimensionCanEdit}
                                    disabledEdit={similarEditDimension === 0.1}
                                    correct={isValidDimension}
                                    setCorrect={setIsValidDimension}
                                    showMinMaxOnInvalidRange
                                />
                            ) : productInCartData.dimension?.toFixed(3)
                        }
                    </div>
                </td>
                {
                    _.map(selectedProduct.additions, (addition, addIndex) => (
                        <td className={similarEdit === productInCartData.indexInCart && productInCartData.dimension ? 'edit-td' : ''} key={`cartAdditionOnProd${addIndex}`}>
                            {
                                _.some(productInCartData.product.additions, (add) => add.name === addition.name) && productInCartData.additionsSelected ? (
                                    similarEdit === productInCartData.indexInCart ? (
                                        <Form.Check
                                            name={addition.name}
                                            type='checkbox'
                                            defaultChecked={productInCartData.additionsSelected[addIndex]}
                                            disabled={productInCartData.product.additions[addIndex].default}
                                            onChange={() => {
                                                const newArr = [...similarAdditions];
                                                newArr[addIndex] = !similarAdditions[addIndex];
                                                setSimilarAdditions(newArr);
                                                setSavable(similarEditQuantity > 0 && similarEditDimension > 0);
                                            }}
                                        />
                                    ) : productInCartData.additionsSelected[addIndex] ? <FaCheck /> : <MdClose />
                                ) : 'N/A'
                            }
                        </td>
                    ))
                }
                <td>{productInCartData.unit.unit}</td>
                <td>
                    <div className='price-td-div' style={{ width: maxJMPriceWidth, marginRight: '0' }}>
                        {(cartMethods.getOneProductUnitNet(productInCartData)).toFixed(2)}
                    </div>
                </td>
                <td>
                    <div className='price-td-div' style={{ width: maxJMQuantityWidth, marginRight: '0' }}>
                        {cartMethods.getTotalQuantityInSelectedUnit(productInCartData).toFixed(2)}
                    </div>
                </td>
                <td>
                    <div className='price-td-div' style={{ width: maxM2QuantityWidth, marginRight: '0' }}>
                        {cartMethods.getTotalQuantityInM2(productInCartData).toFixed(2)}
                    </div>
                </td>
                <td>
                    <div className='price-td-div' style={{ width: maxGrossPriceWidth, marginRight: '0' }}>
                        {cartMethods.getTotalPriceGross(productInCartData).toFixed(2)}
                    </div>
                </td>
                <td>
                    {
                        similarEdit === productInCartData.indexInCart ?
                            <FaCheck className={`manage-cart-icon-edit fa-check ${!isSavable ? 'disabled-icon' : ''}`} onClick={() => saveProduct(productInCartData)} /> :
                            <FaEdit className='manage-cart-icon-edit' onClick={() => editProduct(productInCartData)} />
                    }
                </td>
                <td>
                    <FaTrashAlt className='similar-products-manage-cart-icon-delete' onClick={() => deleteProduct(productInCartData)} />
                </td>
            </tr>);
    };

    const getSimilarProductRowMobile = (productInCart: ProductInCartTable) => {
        const productInCartData = getProductInCartData(productInCart);

        return (
            <section
                key={`similar_${productInCartData.indexInCart}`}
                className={`block-item ${collapseSimilarDetails[productInCartData.indexInCart] ? 'rotate' : ''}`}
                onClick={() => {
                    const newArr = [...collapseSimilarDetails];
                    newArr[productInCartData.indexInCart] = !newArr[productInCartData.indexInCart];
                    setCollapseSimilarDetails(newArr);
                }}
            >
                <div>
                    <div className='ProductSelection-SimilarProducts-headerData'>
                        <b>
                            {productInCartData.product.description}
                            {' '}
                            <br />
                        </b>
                    </div>
                    <div className='ProductSelection-SimilarProducts-headerData'>
                        <div>
                            Ilość sztuk:
                            {' '}
                            <b>
                                {
                                    similarEdit === productInCartData.indexInCart ? (
                                        <NumberInput
                                            value={similarEditQuantity}
                                            setValue={(value) => setSimilarEditQuantity(value)}
                                            stopPropagation
                                            disabledEdit={similarEditQuantity === 1}
                                        />
                                    ) : productInCartData.quantity
                                }
                            </b>
                        </div>
                        <div>
                            Wymiar:
                            {' '}
                            <b>
                                {
                                    similarEdit === productInCartData.indexInCart && productInCartData.product.isDimension && productInCartData.product.dimensionCanEdit ? (
                                        <NumberInput
                                            value={similarEditDimension}
                                            setValue={(value) => setSimilarEditDimension(value)}
                                            step={0.1}
                                            min={selectedProduct.dimensionMin ?? 0.01}
                                            max={selectedProduct.dimensionMax ?? 10 ** 5}
                                            fractDigits={3}
                                            stopPropagation
                                            disabled={_.isEqual(selectedProduct.unit, 'szt') || !productInCartData.product.dimensionCanEdit}
                                            disabledEdit={similarEditDimension === 0.01}
                                            correct={isValidDimension}
                                            setCorrect={setIsValidDimension}
                                            showMinMaxOnInvalidRange
                                        />
                                    ) : (productInCartData.dimension ?? 1).toFixed(3)
                                }
                            </b>
                        </div>
                    </div>
                </div>
                <Collapse in={collapseSimilarDetails[productInCartData.indexInCart]}>
                    <section>
                        {
                            _.map(selectedProduct.additions, (addition, addIndex) => (
                                <AdditionalProducts
                                    addIndex={addIndex}
                                    addition={addition}
                                    index={productInCartData.indexInCart}
                                    product={productInCartData}
                                    similarEdit={similarEdit}
                                    change={() => {
                                        const newArr = [...similarAdditions];
                                        newArr[addIndex] = !similarAdditions[addIndex];
                                        setSimilarAdditions(newArr);
                                    }}
                                />
                            ))
                        }
                        <div>
                            <span>JM: </span>
                            <hr />
                            <b>{productInCartData.product.unit}</b>
                        </div>
                        <div>
                            <span>Cena za JM: </span>
                            <hr />
                            <b>{cartMethods.getProductPriceAfterDiscountPerDefaultUnit(productInCartData).toFixed(2)}</b>
                        </div>
                        <div>
                            <span>Ilość w JM: </span>
                            <hr />
                            <b>{cartMethods.getTotalQuantityInSelectedUnit(productInCartData).toFixed(2)}</b>
                        </div>
                        <div>
                            <span>Ilość w M^2: </span>
                            <hr />
                            <b>{cartMethods.getTotalQuantityInM2(productInCartData).toFixed(2)}</b>
                        </div>
                        <div>
                            <span>Brutto: </span>
                            <hr />
                            <b>{cartMethods.getTotalPriceGross(productInCartData).toFixed(2)}</b>
                        </div>
                        <div>
                            {
                                similarEdit === productInCartData.indexInCart ?
                                    <FaCheck
                                        className={`manage-cart-icon-edit fa-check ${isSavable ? 'active-icon' : 'disabled-icon'}`}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            saveProduct(productInCartData);
                                        }}
                                    /> :
                                    <FaEdit
                                        className='manage-cart-icon-edit'
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            editProduct(productInCartData);
                                        }}
                                    />
                            }
                            <FaTrashAlt className='similar-products-manage-cart-icon-delete' onClick={() => deleteProduct(productInCartData)} />
                        </div>
                    </section>
                </Collapse>
            </section>
        );
    };

    useEffect(() => {
        setCollapseSimilarDetails(Array<boolean>(similarProductCount).fill(false));
    }, [similarProductCount]);

    useEffect(() => {
        setSavable(similarEditQuantity > 0 && similarEditDimension > 0 && isValidDimension);
    }, [similarEditQuantity, similarEditDimension, isValidDimension]);

    useEffect(() => {
        const products: ProductInCartTable[] = _.filter(_.map(localCart.productsInLocalCart, (prd, index) => cartMethods.mapProductInLocalCartToProductInCartTable(prd, index)), (prd) => prd.product.path.join(' ') === makeGroupName());
        setSimilarProducts(products);
    }, [localCart, selectedProduct]);

    useEffect(() => {
        const similarProductsMostRecentValues = _.map(similarProducts,
            (p) => {
                if (p.indexInCart === similarEdit) {
                    return {
                        ...p,
                        quantity: similarEditQuantity,
                        dimension: similarEditDimension,
                        additionsSelected: similarAdditions,
                        quantityJm: totalSimilarQuantity
                    };
                }
                return p;
            });
        setMaxQuantity(_.reduce(similarProductsMostRecentValues, (max, item) => _.max([item.quantity, max]) || max, 0));
        setMaxDimension(_.reduce(similarProductsMostRecentValues, (max, item) => _.max([item.dimension, max]) || max, 0));
        setMaxJMPrice(_.reduce(similarProductsMostRecentValues, (max, item) => _.max([+cartMethods.getProductPriceAfterDiscountPerDefaultUnit(item) * (item.unit.converter ?? 1), max]) || max, 0));
        setMaxJMQuantity(_.reduce(similarProductsMostRecentValues, (max, item) => _.max([item.quantityJm, max]) || max, 0));
        setMaxM2Quantity(_.reduce(similarProductsMostRecentValues, (max, item) => _.max([cartMethods.getTotalQuantityInM2(item), max]) || max, 0));
        setMaxGrossPrice(_.reduce(similarProductsMostRecentValues, (max, item) => _.max([+cartMethods.getTotalPriceGross(item), max]) || max, 0));
    }, [similarProducts, similarAdditions, similarEditDimension, similarEditQuantity]);

    if (_.isEmpty(similarProducts)) {
        return null;
    }

    const getSimilarProductsHeader = () => {
        const showEffectiveMetersCol = _.find(similarProducts, (product) => product.product.unitM2 === 'm2e');
        const showTotalMetersCol = _.find(similarProducts, (product) => product.product.unitM2 === 'm2c');
        const showMetersCol = _.find(similarProducts, (product) => product.product.unitM2 !== 'm2e' && product.product.unitM2 !== 'm2c');

        return (
            <tr>
                <th className='ProductSelection-similarProductsTable-description ProductSelection-similarProductsTable-field'>Nazwa</th>
                <th className='ProductSelection-similarProductsTable-quantity ProductSelection-similarProductsTable-field'>Ilość</th>
                <th className='ProductSelection-similarProductsTable-dimension ProductSelection-similarProductsTable-field'>Wymiar</th>
                {
                    _.map(_.concat(selectedProduct.additions), (addition) => (
                        <th key={`addition${addition.name}`} className='ProductSelection-similarProductsTable-additions ProductSelection-similarProductsTable-field'>{addition.name}</th>
                    ))
                }
                <th className='ProductSelection-similarProductsTable-measurement-unit ProductSelection-similarProductsTable-field'>JM</th>
                <th className='dimension ProductSelection-similarProductsTable-field ProductSelection-similarProductsTable-price'>
                    Cena
                    <br />
                    za JM
                </th>
                <th className='ProductSelection-similarProductsTable-measurement-unit-quantity ProductSelection-similarProductsTable-field'>
                    Ilość
                    <br />
                    w JM
                </th>
                {
                    showEffectiveMetersCol && (
                        <th className='ProductSelection-similarProductsTable-square-meters-quantity ProductSelection-similarProductsTable-field'>
                            Ilość w metrach efekt.
                        </th>
                    )
                }
                {
                    showTotalMetersCol && (
                        <th className='ProductSelection-similarProductsTable-square-meters-quantity ProductSelection-similarProductsTable-field'>
                            Ilość w metrach całk.
                        </th>
                    )
                }
                {
                    showMetersCol && (
                        <th className='ProductSelection-similarProductsTable-square-meters-quantity ProductSelection-similarProductsTable-field'>
                            Ilość w metrach
                        </th>
                    )
                }
                <th className='ProductSelection-similarProductsTable-gross-price ProductSelection-similarProductsTable-field'>BRUTTO</th>
                <th className='ProductSelection-similarProductsTable-icon' />
                <th className='ProductSelection-similarProductsTable-icon' />
            </tr>
        );
    };

    return isDesktop ? (
        <div className='AssortmentSelection-similarProductsTable-container'>
            <h4 style={{ marginBottom: '8px', marginTop: '8px' }}>Podobne produkty znajdujące się w koszyku</h4>
            <Table size='sm' borderless striped className='ProductSelection-similarProductsTable'>
                <thead>
                    {getSimilarProductsHeader()}
                </thead>
                <tbody>
                    {_.map(_.slice(similarProducts, 0, MAX_SIMILAR_COUNT_SHOW), (productInCartTable) => getSimilarProductRow(productInCartTable))}
                    {showMoreSimilar && _.map(_.slice(similarProducts, MAX_SIMILAR_COUNT_SHOW), (productInCart) => getSimilarProductRow(productInCart))}
                </tbody>
            </Table>
            {
                similarProducts.length > MAX_SIMILAR_COUNT_SHOW && (
                    <div className='SimilarProducts-collapse-arrow-box' style={{ cursor: 'pointer' }} onClick={() => setShowMoreSimilar(!showMoreSimilar)}>
                        {showMoreSimilar ? <FaAngleUp size='35px' /> : <FaAngleDown size='35px' />}
                    </div>
                )
            }
        </div>
    ) : (
        <div className='ProductSelection-collapseSimilar' ref={similarProductsRef}>
            <div className='ProductSelection-addProductHeader' onClick={() => setCollapseSimilar(!collapseSimilar)}>
                <b>Podobne produkty w koszyku</b>
                {collapseSimilar ? <FaAngleUp size='40px' /> : <FaAngleDown size='40px' />}
            </div>
            <Collapse in={collapseSimilar} onEntered={handleScrollTo}>
                <div>
                    {_.map(similarProducts, (productInCart) => (getSimilarProductRowMobile(productInCart)))}
                </div>
            </Collapse>
        </div>
    );
};

export default SimilarProducts;
